import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import {
    employeeAdvanceApi,
    employeeApi,
} from "../../components/dashboard/components/AdminComponent/EmployeeManagement/utils/employeeApi";


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    employees:
        [],

    dropdownData: {

        departments:
            [],

        designations:
            [],

        statuses:
            [],

        employmentTypes:
            [],

        roles:
            [],

        permissions:
            [],

        permissionModules:
            [],

    },

    pagination: {

        page:
            1,

        limit:
            10,

        totalRecords:
            0,

        totalPages:
            1,

    },

    listLoading:
        false,

    dropdownLoading:
        false,

    error:
        null,

};


/*
|--------------------------------------------------------------------------
| DROPDOWN
|--------------------------------------------------------------------------
*/

export const fetchEmployeeDropdown =
    createAsyncThunk(
        "employee/fetchDropdown",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await employeeApi
                        .getDropdownData();


                if (
                    response?.status !==
                    "success"
                ) {

                    return rejectWithValue(
                        response?.message ||
                        "Unable to load employee dropdown data."
                    );

                }


                return response
                    ?.data ||
                    {};

            } catch (
                error
            ) {

                return rejectWithValue(

                    error
                        ?.response
                        ?.data
                        ?.message ||

                    error
                        ?.message ||

                    "Unable to load employee dropdown data."

                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| EMPLOYEE LIST
|--------------------------------------------------------------------------
*/

export const fetchEmployees =
    createAsyncThunk(
        "employee/fetchEmployees",

        async (
            payload = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await employeeApi
                        .getEmployees(
                            payload
                        );


                if (
                    response?.status !==
                    "success"
                ) {

                    return rejectWithValue(
                        response?.message ||
                        "Unable to load employees."
                    );

                }


                return {

                    employees:
                        Array.isArray(
                            response
                                ?.data
                        )
                            ?
                            response.data
                            :
                            [],

                    pagination:
                        response
                            ?.pagination ||
                        {},

                };

            } catch (
                error
            ) {

                return rejectWithValue(

                    error
                        ?.response
                        ?.data
                        ?.message ||

                    error
                        ?.message ||

                    "Unable to load employees."

                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const employeeSlice =
    createSlice({

        name:
            "employee",

        initialState,

        reducers: {

            clearEmployeeError:
                (
                    state
                ) => {

                    state.error =
                        null;

                },

        },

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | DROPDOWN
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchEmployeeDropdown
                            .pending,
                        (
                            state
                        ) => {

                            state.dropdownLoading =
                                true;

                            state.error =
                                null;

                        }
                    )
                    .addCase(
                        fetchEmployeeDropdown
                            .fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.dropdownLoading =
                                false;


                            state.dropdownData = {

                                departments:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.departments
                                    )
                                        ?
                                        action
                                            .payload
                                            .departments
                                        :
                                        [],

                                designations:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.designations
                                    )
                                        ?
                                        action
                                            .payload
                                            .designations
                                        :
                                        [],

                                statuses:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.statuses
                                    )
                                        ?
                                        action
                                            .payload
                                            .statuses
                                        :
                                        [],

                                employmentTypes:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.employmentTypes
                                    )
                                        ?
                                        action
                                            .payload
                                            .employmentTypes
                                        :
                                        [],

                                roles:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.roles
                                    )
                                        ?
                                        action
                                            .payload
                                            .roles
                                        :
                                        [],

                                permissions:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.permissions
                                    )
                                        ?
                                        action
                                            .payload
                                            .permissions
                                        :
                                        [],

                                permissionModules:
                                    Array.isArray(
                                        action
                                            .payload
                                            ?.permissionModules
                                    )
                                        ?
                                        action
                                            .payload
                                            .permissionModules
                                        :
                                        [],

                            };

                        }
                    )
                    .addCase(
                        fetchEmployeeDropdown
                            .rejected,
                        (
                            state,
                            action
                        ) => {

                            state.dropdownLoading =
                                false;

                            state.error =
                                action.payload;

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | LIST
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchEmployees
                            .pending,
                        (
                            state
                        ) => {

                            state.listLoading =
                                true;

                            state.error =
                                null;

                        }
                    )
                    .addCase(
                        fetchEmployees
                            .fulfilled,
                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;


                            state.employees =
                                action
                                    .payload
                                    ?.employees ||
                                [];


                            state.pagination = {

                                ...state.pagination,

                                ...(
                                    action
                                        .payload
                                        ?.pagination ||
                                    {}
                                ),

                            };

                        }
                    )
                    .addCase(
                        fetchEmployees
                            .rejected,
                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;

                            state.error =
                                action.payload;

                        }
                    );

            },

    });


export const {
    clearEmployeeError,
} =
    employeeSlice.actions;


export const selectEmployees =
    (
        state
    ) =>
        state.employee
            ?.employees ||
        [];


export const selectEmployeeDropdown =
    (
        state
    ) =>
        state.employee
            ?.dropdownData ||
        initialState.dropdownData;


export const selectEmployeePagination =
    (
        state
    ) =>
        state.employee
            ?.pagination ||
        initialState.pagination;


export const selectEmployeeListLoading =
    (
        state
    ) =>
        Boolean(
            state.employee
                ?.listLoading
        );


export const selectEmployeeDropdownLoading =
    (
        state
    ) =>
        Boolean(
            state.employee
                ?.dropdownLoading
        );


export const selectEmployeeError =
    (
        state
    ) =>
        state.employee
            ?.error ||
        null;


export default employeeSlice.reducer;